import React from 'react'
import { useAppVersionHistory } from '@/shared/hooks'
import type { AppPlatform, AppVersionHistoryItem } from '@/shared/api'
import { Card } from '@/shared/ui'

const UPDATE_TYPE_LABELS: Record<string, string> = {
  off: 'Выключено',
  recommended: 'Рекомендуемое обновление',
  forced: 'Обязательное обновление',
}

const ACTION_LABELS: Record<AppVersionHistoryItem['action'], string> = {
  saved: 'Сохранение',
  activated: 'Включение',
  deactivated: 'Отключение',
}

const FIELD_LABELS: Record<string, string> = {
  platform: 'Платформа',
  is_active: 'Кампания активна',
  update_type: 'Тип обновления',
  min_version: 'Минимальная версия',
  latest_version: 'Актуальная версия',
  store_url: 'Ссылка на магазин',
  forced_title: 'Заголовок обязательного обновления',
  forced_message: 'Текст обязательного обновления',
  recommended_title: 'Заголовок рекомендуемого обновления',
  recommended_message: 'Текст рекомендуемого обновления',
}

const PLATFORM_VALUES: Record<string, string> = {
  android: 'Android',
  ios: 'iOS',
}

const formatValue = (field: string, value: string): string => {
  if (!value) return '—'
  if (field === 'is_active') return value === 'true' ? 'да' : 'нет'
  if (field === 'update_type') {
    return UPDATE_TYPE_LABELS[value] ?? value
  }
  if (field === 'platform') return PLATFORM_VALUES[value] ?? value
  return value
}

interface AppVersionHistoryProps {
  platform: AppPlatform
}

export const AppVersionHistory: React.FC<AppVersionHistoryProps> = ({ platform }) => {
  const { data, isFetching, isError } = useAppVersionHistory(platform)
  const items = data?.items ?? []
  const isLoading = isFetching && !data

  return (
    <Card>
      <h2 className="text-base font-semibold text-gray-900">История изменений</h2>

      {isLoading && <p className="mt-4 text-sm text-gray-500">Загрузка истории…</p>}

      {isError && (
        <p className="mt-4 text-sm text-red-600">Не удалось загрузить историю изменений</p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">Изменений пока нет</p>
      )}

      {!isError && items.length > 0 && (
        <ul className="mt-4 space-y-3">
          {items.map((item) => (
            <li key={item.id} className="rounded-md border border-gray-200 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {ACTION_LABELS[item.action] ?? item.action}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(item.created_at * 1000).toLocaleString('ru-RU')}
                </span>
              </div>

              <p className="mt-1 text-xs text-gray-500">{item.admin_name || 'Система'}</p>

              {(item.changes ?? []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {(item.changes ?? []).map((change, index) => (
                    <li
                      key={`${change.field}-${index}`}
                      className="text-xs text-gray-600"
                    >
                      <span className="font-medium text-gray-700">
                        {FIELD_LABELS[change.field] ?? change.field}
                      </span>
                      {': '}
                      <span className="text-gray-500">
                        {formatValue(change.field, change.old_value)}
                      </span>
                      {' → '}
                      <span className="text-gray-900">
                        {formatValue(change.field, change.new_value)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
